"use client"

import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { InfoIcon, Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"
import React, { useEffect, useState } from "react"
import { useSWRConfig } from "swr"

import { buyCouponAction, cancelDineInRequestAction, requestDineInAction } from "@/app/[locale]/customer/actions"
import type { API_ROUTES_ORDER_RETURN } from "@/app/api/order/route.Utils"
import { API_ROUTES_ORDER } from "@/app/api/order/route.Utils"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { CONST } from "@/constants"
import type { CustomerOngoingOrderByStatusLoader } from "@/data/customerDataLoader"
import { usePendingOrder } from "@/hooks/customer/usePendingOrder"
import { useToast } from "@/hooks/use-toast"
import { CartContent, type CartItem, useCart } from "@/hooks/useCart"
import { usePolling } from "@/hooks/usePolling"
import { useServerAction } from "@/hooks/useServerAction"
import { useRouter } from "@/i18n/routing"
import type { CustomerOrder } from "@/lib/zenstack/models"
import { ROUTES } from "@/routes"
import { ClientUtils } from "@/utils/client/ClientUtils"
import { PriceSharedUtils } from "@/utils/shared/PriceSharedUtils"

import { Tip } from "../Tip"
import { CustomerWalletRedirectForPaymentQuery } from "./CustomerWallet"
import { useLocale } from "@/hooks/useLocale"
import { PaymentMethodSelector } from "./PaymentMethodSelector"

interface CustomerRequestSessionProps {
  isOpen: boolean
  onClose: () => void
  cartItems: CartItem[]
  cartMerchantId: string
  cartMerchantName: string
  cartType: CartContent["cartType"]
  calculateTotalPriceDollars: () => number
  calculateTotalEffectivePriceDollars: () => number
  pendingOrder: CustomerOngoingOrderByStatusLoader | null
  couponMerchant: boolean
}

export function CustomerRequestSession({
  isOpen,
  onClose,
  cartItems,
  cartMerchantId,
  cartMerchantName,
  cartType,
  calculateTotalPriceDollars,
  calculateTotalEffectivePriceDollars,
  pendingOrder,
  couponMerchant,
}: CustomerRequestSessionProps) {
  const t = useTranslations("customer")
  const locale = useLocale()
  const {
    isPending: isRequestPending,
    serverActionSync: requestDineIn,
    response: requestDineInResponse,
  } = useServerAction(requestDineInAction)
  const { isPending: isBuyCouponPending, serverActionSync: buyCoupon } = useServerAction(buyCouponAction)
  const { isPending: isCancelPending, serverActionSync: cancelDineIn } = useServerAction(cancelDineInRequestAction)
  const [requestSent, setRequestSent] = useState(!!pendingOrder)
  const { toast } = useToast()
  const router = useRouter()
  const { clearCart, getCartMerchantDefaultDiscount } = useCart()
  const [createdOrder, setCreatedOrder] = useState<CustomerOrder | null>(pendingOrder)
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>()

  // Used to invalidate the usePendingOrder hook (used in CustomerCart)
  const { mutate } = useSWRConfig()
  const { key } = usePendingOrder()

  usePolling(
    null,
    async () => {
      if (createdOrder && createdOrder.orderStatus === "PENDING") {
        const res = await fetch(API_ROUTES_ORDER.get(createdOrder.id))
        const data = (await res.json()) as API_ROUTES_ORDER_RETURN
        setCreatedOrder(data)
        return data
      }
      return null
    },
    {
      interval: createdOrder ? 2000 : 0,
    }
  )

  useEffect(() => {
    if (requestDineInResponse?.metadata?.paymentMethods === "NO_PAYMENT_METHOD") {
      router.push(`${ROUTES.customerWallet}?${CustomerWalletRedirectForPaymentQuery}=true`)
    }
  }, [requestDineInResponse, router])
  useEffect(() => {
    if (createdOrder && createdOrder.orderStatus === "MERCHANT_ACCEPTED") {
      toast({
        title: t("orders.request_session.accepted.title"),
        description: t("orders.request_session.accepted.description"),
      })
      clearCart()
      router.push(ROUTES.customerOngoingOrder)
      onClose()
    } else if (createdOrder && createdOrder.orderStatus === "MERCHANT_NOTIFICATION_EXPIRED") {
      toast({
        title: t("orders.request_session.expired.title"),
        description: t("orders.request_session.expired.description"),
        variant: "destructive",
      })
      onClose()
    }
  }, [createdOrder, onClose, router])

  const handleConfirmDineIn = () => {
    if (cartMerchantId && cartItems.length > 0) {
      if (couponMerchant) {
        buyCoupon({ merchantId: cartMerchantId, cartItems, selectedPaymentMethodId }, (res) => {
          if (res?.success) {
            setRequestSent(true)
          }
        })
      } else {
        requestDineIn({ merchantId: cartMerchantId, cartItems, cartType, selectedPaymentMethodId }, (res) => {
          if (res?.success) {
            mutate(key)
            toast({
              title: t("orders.request_session.sent.title"),
              description: t("orders.request_session.sent.description"),
            })
            setRequestSent(true)
            setCreatedOrder(res.data?.customerOrder ?? null)
          }
        })
      }
    }
  }

  const handleCancelRequest = () => {
    if (requestSent) {
      cancelDineIn(null, (res) => {
        if (res?.success) {
          mutate(key)
          toast({
            title: t("orders.request_session.cancelled.title"),
            description: t("orders.request_session.cancelled.description"),
          })
        }
        onClose()
      })
    }
  }

  const isRequestCancelled = createdOrder && createdOrder.orderStatus === "MERCHANT_REJECTED"
  if (isRequestCancelled) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("orders.request_session.title", { merchantName: cartMerchantName })}</DialogTitle>
            <DialogDescription className="mt-4">
              {t("orders.request_session.pending", { merchantName: cartMerchantName })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              {t("orders.request_session.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  const totalAmountDollars = calculateTotalPriceDollars()
  const totalEffectiveAmountDollars = calculateTotalEffectivePriceDollars()

  const {
    dollars: { totalAmountToPay, tipAmount, gstAmount, hstAmount, stripeFeeAmount },
  } = PriceSharedUtils.calculatePaymentAmounts({
    totalEffectiveAmountCents: PriceSharedUtils.getCentsPrice(totalEffectiveAmountDollars),
    tipRate: CONST.MINIMUM_TIP_RATE,
    couponMerchant,
  })

  if (requestSent && couponMerchant) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <VisuallyHidden>
              <DialogTitle>{t("orders.request_session.title", { merchantName: cartMerchantName })}</DialogTitle>
            </VisuallyHidden>
            <VisuallyHidden>
              <DialogDescription></DialogDescription>
            </VisuallyHidden>
            <div className="flex flex-col items-center gap-4">
              <CircleCheckIcon className="size-16 text-green-600" />
              <h3 className="text-lg font-medium">{t("orders.request_session.codes.order_confirmed")}</h3>
              <p className="text-center">{t("orders.request_session.codes.description")}</p>
              <Button
                className="mt-4"
                onClick={() => {
                  clearCart()
                  router.push(ROUTES.customerCodes)
                }}
              >
                {t("orders.request_session.codes.view_codes")}
              </Button>
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("orders.request_session.title", { merchantName: cartMerchantName })}</DialogTitle>
          <DialogDescription hidden>
            {t("orders.request_session.title", { merchantName: cartMerchantName })}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 max-h-[50vh] space-y-4 overflow-y-auto">
          {!couponMerchant && (
            <div className="space-y-3">
              <PaymentMethodSelector
                selectedPaymentMethodId={selectedPaymentMethodId}
                onPaymentMethodSelect={setSelectedPaymentMethodId}
                horizontal={true}
              />
            </div>
          )}
          {cartItems.map((item) => (
            <div key={item.id} className="flex items-center justify-between">
              <div>
                <div>
                  <p className="font-medium">
                    {ClientUtils.getLocalizedValue(locale, {
                      en: item.dish.nameEN,
                      fr: item.dish.nameFR,
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("orders.request_session.quantity", { number: item.quantity })}
                  </p>
                </div>
                <div>
                  {item.addOns.map((addOn) => (
                    <div className="flex items-center gap-1" key={addOn.id}>
                      <p className="text-sm">
                        {ClientUtils.getLocalizedValue(locale, {
                          en: addOn.nameEN,
                          fr: addOn.nameFR,
                        })}
                      </p>
                      <p className="text-sm text-muted-foreground line-through">
                        ${ClientUtils.formatPrice(PriceSharedUtils.getDollarsPrice(addOn.priceCents))}
                      </p>
                      <p className="text-sm font-bold">
                        $
                        {ClientUtils.formatPrice(
                          PriceSharedUtils.getDollarsPrice(
                            PriceSharedUtils.computeEffectivePriceCents(
                              addOn.priceCents,
                              getCartMerchantDefaultDiscount() ?? 0
                            )
                          )
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!couponMerchant && (
                  <p className="text-sm text-muted-foreground line-through">
                    ${ClientUtils.formatPrice(PriceSharedUtils.getDollarsPrice(item.dish.priceCents))}
                  </p>
                )}
                <p className="font-medium">
                  $
                  {ClientUtils.formatPrice(
                    PriceSharedUtils.getDollarsPrice(
                      PriceSharedUtils.computeEffectivePriceCents(item.dish.priceCents, item.dish.discount)
                    )
                  )}
                </p>
              </div>
            </div>
          ))}

          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <span className="">{t("orders.request_session.subtotal")}:</span>
              <div>
                {!couponMerchant && (
                  <span className="mr-2 text-sm text-muted-foreground line-through">
                    ${ClientUtils.formatPrice(totalAmountDollars)}
                  </span>
                )}
                <span className="text-md">${ClientUtils.formatPrice(totalEffectiveAmountDollars)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="">{t("orders.request_session.tip")}:</span>
              <span className="text-md">${ClientUtils.formatPrice(tipAmount)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="">{t("orders.request_session.gst")}:</span>
              <div>
                <span className="text-md ">${ClientUtils.formatPrice(gstAmount + hstAmount)}</span>
              </div>
            </div>
            {!couponMerchant && (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  {t("orders.ongoing.summary.processing_fees.title")}{" "}
                  <Tip content={t("orders.ongoing.summary.processing_fees.description")}>
                    <InfoIcon className="size-4" />
                  </Tip>
                </span>
                <div>
                  <span className="text-md ">${ClientUtils.formatPrice(stripeFeeAmount.customer)}</span>
                </div>
              </div>
            )}
            <div className="my-4 flex items-center justify-between">
              <span className="font-medium">{t("orders.request_session.total")}:</span>
              <span className="text-lg font-medium">${ClientUtils.formatPrice(totalAmountToPay)}</span>
            </div>
            <div className="border-t pt-4" />
            {!couponMerchant && (
              <span className="text-md font-bold">
                {t("orders.request_session.estimated_saving")}{" "}
                <span className="text-green-600">
                  $
                  {ClientUtils.formatPrice(
                    (totalAmountDollars - totalEffectiveAmountDollars) * CONST.ESTIMATED_SAVING_MULTIPLIER
                  )}
                </span>
              </span>
            )}
            {couponMerchant && (
              <span className="flex items-center gap-1.5 rounded-md bg-muted px-3 py-1.5 text-sm text-muted-foreground">
                <InfoIcon className="size-4" />
                {t("orders.request_session.codes_non_refundable")}
              </span>
            )}
          </div>
        </div>

        <DialogFooter className="mt-6">
          {requestSent ? (
            <Button variant="destructive" onClick={handleCancelRequest} disabled={isCancelPending}>
              {isCancelPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                t("orders.request_session.actions.cancel")
              )}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>
                {t("orders.request_session.close")}
              </Button>
              <Button onClick={handleConfirmDineIn} disabled={isRequestPending || isBuyCouponPending}>
                {isRequestPending || isBuyCouponPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  t("orders.request_session.actions.confirm")
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CircleCheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )
}
